import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, FileText, ShoppingBag, Download, Music, Video, Newspaper, Image as ImageIcon, ExternalLink, Layers } from 'lucide-react';

/**
 * ContentTypePicker
 * -----------------
 * Reusable modal shown BEFORE the content editor opens. Presents a Content
 * Type selector so admins can add any type of content inside any channel.
 *
 * Content Types (per Universal Content Creation spec):
 *   • Standard Content
 *   • Product
 *   • Digital Download
 *   • Audio
 *   • Video
 *   • Article
 *   • Gallery Item
 *   • External Link
 *   • (Future content types — represented by disabled placeholder tile)
 *
 * Channels organize content. Content Types determine behavior. Products can be
 * created inside ANY channel — Product is not restricted to a dedicated store.
 *
 * Props:
 *   open              boolean            — is the picker visible
 *   onClose           () => void         — close without selecting
 *   onSelect          (typeKey) => void  — user selected a content type
 *   channelKey        string             — the channel this action originated from (metadata only)
 *   channelLabel      string             — pretty name shown in the header
 *   nativeType        string             — the channel's native content type; highlighted as recommended
 *   allowedTypes      string[]           — optional whitelist of type keys (default: all)
 *   title             string             — override modal title
 */

export const CONTENT_TYPES = [
  {
    key: 'standard',
    label: 'Standard Content',
    description: 'A general content block. Ideal for hero cards, notes, or featured highlights.',
    icon: Layers,
  },
  {
    key: 'product',
    label: 'Product',
    description: 'Sellable item. Auto-initializes the full commerce workflow (pricing, fulfillment, checkout, discovery, template).',
    icon: ShoppingBag,
    emphasize: true,
  },
  {
    key: 'digital_download',
    label: 'Digital Download',
    description: 'Delivered as a downloadable file after purchase. Sets fulfillment = Digital Download.',
    icon: Download,
  },
  {
    key: 'audio',
    label: 'Audio',
    description: 'Music, podcast, or reflection. Uploaded file or external embed.',
    icon: Music,
  },
  {
    key: 'video',
    label: 'Video',
    description: 'YouTube or Vimeo embed with poster art.',
    icon: Video,
  },
  {
    key: 'article',
    label: 'Article',
    description: 'Long-form writing with rich text, cover, tags, and slug.',
    icon: Newspaper,
  },
  {
    key: 'gallery',
    label: 'Gallery Item',
    description: 'Single image with caption for a gallery grid.',
    icon: ImageIcon,
  },
  {
    key: 'external_link',
    label: 'External Link',
    description: 'A link tile that redirects visitors to an external URL.',
    icon: ExternalLink,
  },
];

export default function ContentTypePicker({
  open,
  onClose,
  onSelect,
  channelKey,
  channelLabel,
  nativeType,
  allowedTypes,
  title,
}) {
  const filtered = Array.isArray(allowedTypes) && allowedTypes.length > 0
    ? CONTENT_TYPES.filter((t) => allowedTypes.includes(t.key))
    : CONTENT_TYPES;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="content-type-picker"
        >
          <div className="absolute inset-0 bg-[rgba(7,11,20,0.72)] backdrop-blur-sm" />

          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto pg-scroll bg-[var(--pg-navy-900)] border border-[var(--pg-border-2)] rounded-[var(--pg-radius-lg)] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-[var(--pg-border-2)] flex items-start justify-between gap-4 sticky top-0 bg-[var(--pg-navy-900)] z-10">
              <div>
                <p className="pg-label text-[10px]">
                  {channelLabel ? `${channelLabel} · ADD CONTENT` : 'ADD CONTENT'}
                </p>
                <h3 className="font-serif text-2xl mt-1 text-[var(--pg-ink)]">
                  {title || 'Choose a content type'}
                </h3>
                <p className="text-xs text-[var(--pg-ink-muted)] mt-1 max-w-2xl">
                  Channels organize content. Content types determine behavior. Any channel can hold any content type — including commerce-enabled Products.
                </p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                data-testid="content-type-picker-close"
                className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* Grid */}
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((t) => {
                const Icon = t.icon || FileText;
                const isNative = nativeType && t.key === nativeType;
                const isEmphasized = !!t.emphasize;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => onSelect?.(t.key)}
                    data-testid={`content-type-${t.key}`}
                    className={[
                      'text-left group relative pg-card p-4 transition-colors',
                      'hover:border-[var(--pg-emerald)] hover:bg-[rgba(25,194,142,0.06)]',
                      isNative ? 'border-[var(--pg-gold-2)] bg-[rgba(226,199,122,0.08)]' : '',
                      isEmphasized && !isNative ? 'border-[var(--pg-emerald)] bg-[rgba(25,194,142,0.06)]' : '',
                    ].join(' ')}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-[var(--pg-border-2)] bg-black/30 text-[var(--pg-ink)]">
                        <Icon size={16} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-base text-[var(--pg-ink)] truncate">{t.label}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {isNative && (
                            <span className="text-[9px] font-mono tracking-widest uppercase text-[var(--pg-gold-2)]">
                              Recommended
                            </span>
                          )}
                          {isEmphasized && !isNative && (
                            <span className="text-[9px] font-mono tracking-widest uppercase text-[var(--pg-emerald-2)]">
                              Commerce enabled
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-[var(--pg-ink-muted)] leading-relaxed">{t.description}</p>
                  </button>
                );
              })}

              {/* Future content types placeholder — visible but disabled */}
              <div
                className="pg-card p-4 opacity-50 border-dashed"
                data-testid="content-type-future"
                title="Additional content types can be added by extending the platform's Universal Content system."
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-dashed border-[var(--pg-border-2)] text-[var(--pg-ink-faint)]">
                    <Layers size={16} />
                  </span>
                  <div>
                    <p className="font-serif text-base text-[var(--pg-ink-faint)]">Future Content Types</p>
                    <span className="text-[9px] font-mono tracking-widest uppercase text-[var(--pg-ink-faint)]">Extensible</span>
                  </div>
                </div>
                <p className="text-xs text-[var(--pg-ink-faint)] leading-relaxed">
                  The Universal Content System supports adding new content types over time without altering the CMS architecture.
                </p>
              </div>
            </div>

            {/* Footer note */}
            <div className="px-6 py-3 border-t border-[var(--pg-border-2)] text-[10px] font-mono tracking-widest uppercase text-[var(--pg-ink-faint)] sticky bottom-0 bg-[var(--pg-navy-900)]">
              {channelKey ? <>Target channel: <span className="text-[var(--pg-ink-muted)]">{channelKey}</span></> : 'Universal Content · Content type drives behavior'}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
